<template>
  <ListComponent title="Customers" :totalCount="totalItems">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="4" class="px-0 pt-0 pb-2 d-flex align-center">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchCustomers"
          ></v-text-field>

          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFilterDialog = true"
                class="mx-2"
              >
                <v-badge
                  dot
                  color="primary"
                  :value="Object.keys(filters).length"
                >
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>
            <span v-if="Object.keys(filters).length == 0">Filters</span>

            <v-row v-else no-gutters>
              <v-col cols="12" class="font-weight-bold">Applied Filters</v-col>
              <v-col cols="12">
                <span
                  v-for="(value, key) in filters"
                  :key="key"
                  class="text-capitalize"
                  >{{ key.replace("_", " ") }}
                  <br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>

          <Columns
            :headers="headers"
            :gridColInstance="gridColumnApi"
            localStorageKey="customerCompanyColumns"
          />
        </v-col>

        <v-col
          v-if="totalItems > 0"
          cols="8"
          class="px-0 pt-0 pb-2 d-flex justify-end"
        >
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>

        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 172px)"
            class="ag-theme-alpine"
            id="customerCompanyList"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            @columnMoved="resizeGrid"
            @columnVisible="resizeGrid"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="allCustomerList"
          ></AgGridVue>
        </v-col>
      </v-row>

      <DetailsParent v-model="detailsDrawer" :resizable="true">
        <template #details>
          <CustomerDetails
            :customerId="selectedCustomerId"
            :permissions="permissions"
            :userPermissions="userPermissions"
          />
        </template>
      </DetailsParent>
    </template>

    <template #dialogs>
      <AdminCustomerFilters
        v-model="showFilterDialog"
        :bindingObject="filters"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(20)"
      />
      <RenewConfirmationDialogue
        v-model="showConfirmationDialogue"
        :status="status"
        :title="title"
        :showRemarks="true"
        :postDate="postDate"
        :text="text"
        :icon="icon"
        @confirmed="updateCustomerActivationStatus"
        @rejected="refreshCalls()"
      />
      <BulkUpload
        v-model="customerComapnyBulkUploadDialog"
        :uploadTo="uploadType"
        @refreshList="itemsPerPageChanged(20)"
      />
      <FuelSurchargeBulkUpload
        v-model="openFuelSurchargeBulkUpload"
        @refreshList="
          itemsPerPageChanged(20);
          detailsDrawer = false;
        "
      />
    </template>

    <template #actions>
      <v-btn
        v-if="permissions && permissions.add"
        @click="addCustomer"
        color="primary"
        depressed
      >
        <v-icon>mdi-plus</v-icon>
        <span>Add Customer</span>
      </v-btn>
      <v-btn
        class="ml-3"
        v-if="permissions && permissions.add"
        @click="openCompanyBulkUpload('company')"
        color="primary"
        depressed
      >
        <v-icon>mdi-plus</v-icon>
        <span>Bulk Upload</span>
      </v-btn>
      <v-btn
        class="ml-3"
        v-if="permissions && permissions.add"
        @click="openFuelSurchargeBulkUpload = true"
        color="primary"
        depressed
      >
        <v-icon>mdi-plus</v-icon>
        <span>Fuel Surcharge</span>
      </v-btn>
    </template>
  </ListComponent>
</template>

<script>
import Columns from "@/components/General/Columns.vue";
import ListComponent from "@/components/General/ListComponent.vue";
import CustomerDetails from "@/components/ModuleBased/details/CustomerDetails.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import CustomerButton from "@/components/AgGridComponents/CustomerButton.vue";
import DetailsParent from "@/components/General/DetailsParent.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import FuelSurchargeBulkUpload from "@/components/ModuleBased/dialogForms/FuelSurchargeBulkUpload.vue";
import AdminCustomerFilters from "@/components/ModuleBased/dialogs/filters/AdminCustomerFilters.vue";
import BulkUpload from "@/components/General/BulkUpload.vue";
import RenewConfirmationDialogue from "@/components/General/RenewConfirmationDialogue.vue";

import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    Columns,
    CustomerDetails,
    AgGridVue,
    Pagination,
    CustomerButton,
    DetailsParent,
    agColumnHeader: CustomHeader,
    AdminCustomerFilters,
    BulkUpload,
    FuelSurchargeBulkUpload,
    RenewConfirmationDialogue,
  },
  data() {
    return {
      sorting: {},
      remarks: "",
      showConfirmationDialogue: false,
      status: "",
      title: "",
      text: "",
      icon: "",
      postDate: "",
      customerPayload: {},

      headers: [
        {
          headerName: "Company Code",
          field: "company_code",
          sortable: true,
          minWidth: 115,
        },
        {
          headerName: "Company Name",
          field: "company_name",
          sortable: true,
          tooltipField: "company_name",
          minWidth: 400,
        },
        {
          headerName: "Address",
          field: "address_line_1",
          sortable: true,
          tooltipField: "address_line_1",
          minWidth: 400,
        },
        {
          headerName: "City",
          field: "city_name",
          sortable: true,
          tooltipField: "city_name",
          minWidth: 90,
        },
        {
          headerName: "Country",
          field: "country_code",
          sortable: true,
          minWidth: 80,
        },
        {
          headerName: "Contact No",
          sortable: true,
          field: "full_company_contact_number",
          tooltipField: "full_company_contact_number",
          minWidth: 150,
        },
        {
          headerName: "Email Id",
          sortable: true,
          field: "company_email",
          tooltipField: "company_email",
          minWidth: 150,
        },
        {
          headerName: "Payment Method",
          field: "payment_method",
          sortable: true,
          minWidth: 115,
        },
         {
          headerName: "Payment Terms",
          field: "payment_terms",
          sortable: true,
          minWidth: 115,
        },
        {
          headerName: `Currency (${this.$globals.currency})`,
          field: "currency",
          sortable: true,
          tooltipField: "currency",
          minWidth: 130,
        },
        {
          headerName: "Is Active Customer",
          field: "is_active",
          sortable: true,
          tooltipField: "is_active",
          minWidth: 115,
        },
        {
          headerName: "CR Number",
          field: "cr_number",
          sortable: true,
          minWidth: 115,
        },
        {
          headerName: "Customer Group",
          field: "customer_group",
          sortable: true,
          tooltipField: "customer_group",
          minWidth: 115,
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "CustomerButton",
          pinned: "right",
          minWidth: 150,
        },
      ],

      allCustomerList: [],
      showConfirmationDialog: false,

      // pagination vars
      pageNo: 1,
      itemsPerPage: 20,
      totalItems: 0,
      filters: {},

      selectedCustomerId: null,
      permissions: null,
      userPermissions: null,

      // bulk vars
      showBulkDialog: false,

      detailsDrawer: false,

      // Filter Vars
      filters: {},
      showFilterDialog: false,

      // bulk vars
      openFuelSurchargeBulkUpload: false,
      showBulkDialog: false,
      uploadType: "company",
      customerComapnyBulkUploadDialog: false,

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },

  methods: {
    openCompanyBulkUpload(type) {
      this.uploadType = type;
      this.customerComapnyBulkUploadDialog = true;
    },
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(20);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchCustomers() {
      this.pageNo = 1;
      this.getCustomerList();
      this.filters = {};
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getCustomerList();
    },
    prevPage() {
      this.pageNo--;
      this.getCustomerList();
    },
    nextPage() {
      this.pageNo++;
      this.getCustomerList();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },

    confirmation(id, isActive) {
      this.is_active_flag = isActive;
      this.showConfirmationDialogue = true;

      this.customerPayload = {
        id: id,
        is_active: isActive === "No",
      };

      const action = isActive === "Yes" ? "De-activate" : "Activate";

      this.status = "primary";
      this.title = `${action} Customer`;
      this.text = `Are you sure you want to ${action} this customer?`;
      this.icon = "mdi-information";
    },
    updateCustomerActivationStatus({ remarks }) {
      this.customerPayload.remarks = remarks;

      this.$api.company
        .updateActiveStatusCustomer(this.customerPayload)
        .then((response) => {
          this.$bus.$emit("showLoader", false);
          this.customerData = response.data;
          this.getCustomerList();
          this.showConfirmationDialog = false;
        })
        .catch((error) => {
          this.formErrors = error.data.error;
          if (error && error.data) {
            const showToast = (message) => {
              this.$bus.$emit("showToastMessage", {
                message,
                color: "error",
                hasActions: true,
              });
            };

            if (error.data.message) {
              showToast(error.data.message);
            } else if (
              error.data.error &&
              error.data.error.non_field_errors &&
              error.data.error.non_field_errors.length
            ) {
              showToast(error.data.error.non_field_errors.join(", "));
            } else {
              showToast("Company Status Update Failed");
            }
          }
          this.getCustomerList();
          this.$bus.$emit("showLoader", false);
        });
    },

    getCustomerList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,

        company_type: "Customer",
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.company
        .getCompanyList(params)
        .then((res) => {
          this.allCustomerList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    addCustomer() {
      this.$router.push("/app/admin/company-management/customer/add");
    },
    editCustomer(id) {
      this.$router.push(`/app/admin/company-management/customer/edit/${id}`);
    },
    deleteCustomer(customer) {
      this.$bus.$emit("showToastMessage", {
        message: "This is a test",
      });
    },
    getCustomerDetails(customer) {
      this.selectedCustomerId = customer.id;
      this.detailsDrawer = true;
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("customer");
      const userPermissions = getPermissions("user");
      const disablePermissions = getPermissions(
        "enable_disable_customer_vendor"
      );
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.permissions = { ...permissions };
        this.userPermissions = { ...userPermissions };
        this.disablePermissions = { ...disablePermissions };
        this.getCustomerList();
      }
    }
  },
};
</script>
